/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  Uses react-intersection-observer and loadable-components to create lazy-loaded
 *               page sections.
 */

import { Box, BoxProps } from '@chakra-ui/react'
import { LoadableComponent } from '@loadable/component'
import { Renderable } from '@zcode/render-props'
import React from 'react'
import { IntersectionOptions, useInView } from 'react-intersection-observer'

export interface LazySectionProps<ForwardProps extends object>
  extends Omit<BoxProps, 'style' | 'classname'>,
    Pick<IntersectionOptions, 'root' | 'rootMargin' | 'threshold'> {
  component: LoadableComponent<ForwardProps>
  fallback?: Renderable<ForwardProps>
  fallbackStyle?: React.CSSProperties
  forceInView: boolean
  forwardProps: ForwardProps
  lazySectionClassName?: string
}

export const LazySection = <ForwardProps extends object>({
  component: Component,
  fallback,
  fallbackStyle,
  forceInView,
  forwardProps,
  lazySectionClassName,
  root,
  rootMargin = '300px',
  threshold,
  ...rest
}: LazySectionProps<ForwardProps>) => {
  const [ref, inView] = useInView({
    root,
    rootMargin,
    threshold,
    triggerOnce: true,
  })
  const show = forceInView || inView
  const style = show ? undefined : fallbackStyle
  return (
    <Box
      as={rest.as ?? 'div'}
      className={lazySectionClassName}
      ref={ref}
      style={style}
      {...rest}
    >
      {show && <Component {...forwardProps} />}
      {!show && fallback && React.createElement(fallback, forwardProps)}
    </Box>
  )
}
